import { create } from "zustand";
import { getMyTeams } from "./apiStore";

// Multi-owner team membership store.
//
// A team member can belong to many owners' teams, each with a different role /
// permission set. This store fetches all active memberships once and lets any
// component resolve "what can I do on THIS owner's agent" by ownerId.
//
// It is the single source of truth for per-owner permissions — replacing the
// old single `localStorage.teamOwnerId` + one global permissions object.

const OWNER_PERMISSIONS = {
  editAgents: true,
  dialer: true,
  viewCallLogs: true,
  viewAppointments: true,
  viewContacts: true,
  manageBilling: true,
};

export const useTeamStore = create((set, get) => ({
  teams: [],           // [{ ownerId, ownerName, ownerCompany, role, permissions }]
  isTeamMember: false,
  loaded: false,
  loading: false,

  // Fetch (or refresh) the membership list. Safe to call repeatedly.
  loadTeams: async () => {
    if (get().loading) return;
    // Not a team member? Skip the network call entirely.
    if (!localStorage.getItem("teamOwnerId") && !localStorage.getItem("token")) return;
    set({ loading: true });
    try {
      const data = await getMyTeams();
      set({
        teams: data?.teams || [],
        isTeamMember: !!data?.isTeamMember,
        loaded: true,
      });
    } catch (err) {
      console.error("[teamStore] loadTeams failed:", err?.message);
      set({ loaded: true });
    } finally {
      set({ loading: false });
    }
  },

  reset: () => set({ teams: [], isTeamMember: false, loaded: false, loading: false }),

  // The membership row for a given owner (or undefined).
  teamForOwner: (ownerId) => get().teams.find((t) => t.ownerId === ownerId),
}));

// ── Permission resolution helpers ────────────────────────────────────────────
// These take the current teams + an owner id and return effective capabilities.
// An OWNER (not a team member) always gets full access.

export function resolvePermissionsForOwner(teams, isTeamMember, ownerId) {
  if (!isTeamMember) return OWNER_PERMISSIONS; // account owner — unrestricted
  const team = teams.find((t) => t.ownerId === ownerId);
  return team?.permissions || {};
}

export function roleForOwner(teams, isTeamMember, ownerId) {
  if (!isTeamMember) return "owner";
  const team = teams.find((t) => t.ownerId === ownerId);
  return team?.role || "support";
}
